import React, { useState,useEffect,useRef,useMemo } from 'react';
import {useLocation,useNavigate} from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import { recurringService } from '../services/recurringService';
import { dbService } from '../services/dbService';
import { format, addDays, addWeeks, addMonths, addQuarters, addYears } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import { createPortal } from 'react-dom';
import { FiCheckCircle, FiDatabase, FiLayers, FiLock, FiRefreshCw } from 'react-icons/fi';
import ConfirmDialog from '../components/ConfirmDialog';
import * as FiIcons from 'react-icons/fi';


// Kept as a private RecurringGenerationProgress module to preserve the original component behavior.
const RecurringGenerationProgress = (()=>{



const stages = [
  { key: 'preparing', label: 'Checking rules', Icon: FiLayers },
  { key: 'planning', label: 'Preparing entries', Icon: FiLayers },
  { key: 'saving', label: 'Saving locally', Icon: FiDatabase },
  { key: 'refreshing', label: 'Updating records', Icon: FiRefreshCw },
  { key: 'complete', label: 'Complete', Icon: FiCheckCircle }
];

/**
 * A true blocking modal: rendered outside #root, with the application marked
 * inert and focus held inside. No backdrop dismiss, Escape, or accidental
 * buttons while the IndexedDB transaction is being committed.
 */
function RecurringGenerationProgress({ progress }) {
  const dialogRef = useRef(null);
  const stage = progress?.stage || 'preparing';
  const percent = Math.min(100, Math.max(0, Number(progress?.percent) || 0));
  const index = Math.max(0, stages.findIndex(item => item.key === stage));

  useEffect(() => {
    const root = document.getElementById('root');
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const previousRootInert = root?.inert;
    const previousHidden = root?.getAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    if (root) { root.inert = true; root.setAttribute('aria-hidden', 'true'); }
    dialogRef.current?.focus();

    const trapKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); }
      if (event.key === 'Tab') {
        event.preventDefault();
        dialogRef.current?.focus();
      }
    };
    document.addEventListener('keydown', trapKey, true);
    return () => {
      document.removeEventListener('keydown', trapKey, true);
      document.body.style.overflow = previousOverflow;
      if (root) {
        root.inert = previousRootInert ?? false;
        if (previousHidden == null) root.removeAttribute('aria-hidden');
        else root.setAttribute('aria-hidden', previousHidden);
      }
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6 bg-slate-950/70 backdrop-blur-[4px]"
      data-testid="recurring-generation-blocker">
      <section
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="generation-progress-heading"
        aria-describedby="generation-progress-description"
        className="w-full max-w-lg rounded-[24px] border border-slate-200 bg-white shadow-2xl outline-none p-5 sm:p-7"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-12 h-12 grid place-items-center rounded-2xl bg-blue-50 text-blue-600">
            {stage === 'complete' ? <FiCheckCircle size={25} /> :
              <FiRefreshCw size={25} className="animate-spin motion-reduce:animate-none" />}
          </div>
          <div className="min-w-0">
            <h2 id="generation-progress-heading" className="text-xl sm:text-2xl font-bold text-slate-900">
              {stage === 'complete' ? 'Transactions prepared' : 'Creating recurring transactions'}
            </h2>
            <p id="generation-progress-description" className="text-sm text-slate-600 mt-2">
              Keep this screen open until your records are safely saved.
            </p>
          </div>
        </div>

        <div className="mt-6" aria-live="polite" aria-atomic="true">
          <div className="flex justify-between items-baseline gap-3">
            <p className="font-semibold text-sm text-slate-800">{progress?.message || 'Checking recurring rules…'}</p>
            <span className="text-xs font-bold text-blue-700 tabular-nums">
              {progress?.indeterminate ? 'Saving…' : percent + '%'}
            </span>
          </div>
          <div role="progressbar" aria-label="Recurring transaction generation"
            aria-valuemin={0} aria-valuemax={100}
            {...(!progress?.indeterminate ? { 'aria-valuenow': percent } : {})}
            className="relative w-full bg-slate-100 rounded-full h-2.5 mt-3 overflow-hidden">
            <div className={'h-full rounded-full bg-blue-600 transition-[width] duration-200 ' +
              (progress?.indeterminate ? 'animate-pulse' : '')}
              style={{ width: progress?.indeterminate ? '100%' : percent + '%' }} />
          </div>
          {stage === 'planning' && progress?.total > 0 && (
            <p className="text-xs text-slate-500 mt-2 tabular-nums">
              {progress.completed || 0} of {progress.total} scheduled occurrences prepared
            </p>
          )}
          {stage === 'saving' && <p className="text-xs text-slate-500 mt-2">
            Saving as one atomic local operation. Google Sheets sync remains a separate action.
          </p>}
        </div>

        <ol className="mt-6 space-y-3" aria-label="Generation stages">
          {stages.map(({ key, label, Icon }, i) => {
            const completed = i < index || stage === 'complete';
            const active = key === stage && stage !== 'complete';
            return (
              <li key={key} className="flex items-center gap-3 text-sm">
                <span className={'w-8 h-8 rounded-full shrink-0 grid place-items-center ' +
                  (completed ? 'bg-emerald-50 text-emerald-700' :
                    active ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-400')}>
                  {completed ? <FiCheckCircle size={18} /> : <Icon size={17} />}
                </span>
                <span className={active ? 'font-semibold text-blue-800' :
                  completed ? 'text-slate-700' : 'text-slate-400'}>{label}</span>
              </li>
            );
          })}
        </ol>
        <div className="mt-6 rounded-xl bg-slate-50 p-3 flex items-start gap-2 text-xs text-slate-600">
          <FiLock className="shrink-0 mt-0.5 text-slate-500" size={15} />
          Controls are temporarily locked to prevent duplicate actions or accidental navigation.
        </div>
      </section>
    </div>,
    document.body
  );
}

return RecurringGenerationProgress;
})();

const { FiPlus, FiRepeat, FiCalendar, FiDollarSign, FiEdit2, FiTrash2, FiPlay, FiCheck, FiClock, FiInfo, FiSettings } = FiIcons;

const Recurring = () => {
  const { state, actions } = useApp();
  const location=useLocation(),navigate=useNavigate();
  const { recurringRules, settings } = state;
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [search, setSearch] = useState(()=>new URLSearchParams(location.search).get('search')||'');
  useEffect(()=>setSearch(new URLSearchParams(location.search).get('search')||''),[location.search]);
  const updateSearch=value=>{
    setSearch(value);
    const params=new URLSearchParams(location.search);
    if(value.trim())params.set('search',value);
    else params.delete('search');
    navigate({pathname:'/recurring',search:params.toString()?'?'+params.toString():''},{replace:true});
  };
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedRule, setSelectedRule] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [generationResult, setGenerationResult] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [generateDailyTransactions, setGenerateDailyTransactions] = useState(true);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: settings.currency,
    }).format(amount);
  };

  const calculateNextOccurrence = (rule) => {
    const startDate = new Date(rule.startDate);
    const now = new Date();
    
    let nextDate = new Date(startDate);
    
    while (nextDate <= now) {
      switch (rule.frequency) {
        case 'daily':
          nextDate = addDays(nextDate, 1);
          break;
        case 'weekly':
          nextDate = addWeeks(nextDate, 1);
          break;
        case 'fortnightly':
          nextDate = addWeeks(nextDate, 2);
          break;
        case 'monthly':
          nextDate = addMonths(nextDate, 1);
          break;
        case 'quarterly':
          nextDate = addQuarters(nextDate, 1);
          break;
        case 'yearly':
          nextDate = addYears(nextDate, 1);
          break;
        default:
          nextDate = addMonths(nextDate, 1);
      }
    }
    
    return nextDate;
  };

  const getCoverageDays = (frequency) => {
    switch (frequency) {
      case 'daily':
        return 1;
      case 'weekly':
        return 7;
      case 'fortnightly':
        return 14;
      case 'monthly':
        return 30;
      case 'quarterly':
        return 90;
      case 'yearly':
        return 365;
      default:
        return 30;
    }
  };

  const calculateDailyAmount = (rule) => {
    const coverageDays = getCoverageDays(rule.frequency);
    return rule.amount / coverageDays;
  };

  // Display-only monthly projections calculated from saved recurring rules.
  // No financial engine, IndexedDB collection, or Google Sheets schema changes.
  const monthlyMultiplier={daily:365.2425/12,weekly:52.1775/12,
    fortnightly:26.08875/12,monthly:1,quarterly:1/3,yearly:1/12};
  const activeRules=useMemo(()=>recurringRules.filter(rule=>
    !rule.endDate || new Date(String(rule.endDate).slice(0,10)+'T23:59:59')>=new Date()
  ),[recurringRules]);
  const monthlyTotals=useMemo(()=>activeRules.reduce((totals,rule)=>{
    const monthly=Math.round((Number(rule.amount)||0)*100*(monthlyMultiplier[rule.frequency]||0));
    if(rule.type==='income')totals.income+=monthly;
    if(rule.type==='expense')totals.expenses+=monthly;
    return totals;
  },{income:0,expenses:0}),[activeRules]);
  const nextScheduled=activeRules.map(rule=>calculateNextOccurrence(rule))
    .filter(date=>Number.isFinite(date?.getTime())).sort((a,b)=>a-b)[0];
  const visibleRules=useMemo(()=>recurringRules.filter(rule=>{
    const term=search.trim().toLowerCase();
    if(term&&![rule.name,rule.category,rule.frequency,rule.payee,rule.person,rule.notes]
      .join(' ').toLowerCase().includes(term))return false;
    if(activeFilter==='income'||activeFilter==='expense')return rule.type===activeFilter;
    if(activeFilter==='monthly'||activeFilter==='yearly')return rule.frequency===activeFilter;
    if(activeFilter==='daily-breakdown')return rule.generateDailyTransactions!==false;
    return true;
  }),[recurringRules,search,activeFilter]);
  const filters=[['all','All'],['income','Income'],['expense','Expense'],
    ['monthly','Monthly'],['yearly','Yearly'],['daily-breakdown','Daily Breakdown']];


  const handleDeleteRule = async (ruleId) => {
    try {
      // Delete from database
      await dbService.deleteRecurringRule(ruleId);
      
      // Delete any generated transactions from this rule
      await recurringService.deleteRecurringTransactions(ruleId);
      
      // Update app state
      actions.deleteRecurringRule(ruleId);
      
      // Reload transactions to reflect deletions
      await actions.loadData();
      
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete recurring rule:', error);
      alert('Failed to delete recurring rule. Please try again.');
    }
  };

  // The same staged blocking overlay wraps both bulk and per-rule generation.
  // Local IndexedDB commit completes before the overlay is dismissed.
  const runGeneration = async generator => {
    if (isGenerating) return;
    setIsGenerating(true);
    setGenerationResult(null);
    setGenerationProgress({stage:'preparing',percent:0,message:'Checking recurring rules…'});
    try {
      const result = await generator(progress => setGenerationProgress(progress));
      setGenerationProgress({stage:'refreshing',percent:96,message:'Refreshing the financial workspace…'});
      await actions.loadData();
      setGenerationProgress({stage:'complete',percent:100,message:'Transactions saved successfully.'});
      await new Promise(resolve => setTimeout(resolve, 150));
      setGenerationResult({
        success:true,
        message:result.generatedCount > 0
          ? `${result.generatedCount} new transactions saved. ${result.skippedDuplicates || 0} duplicates skipped. Google will back up these local changes when connected and authorised; you can also use Sync Now.`
          : 'No new transactions were required. Existing recurring entries were preserved.'
      });
      return true;
    } catch (error) {
      console.error('Recurring transaction generation failed:', error);
      setGenerationResult({success:false,message:error.message || 'Unable to complete transaction generation.'});
      return false;
    } finally {
      setGenerationProgress(null);
      setIsGenerating(false);
    }
  };

  const handleAutoGenerate = () => runGeneration(onProgress =>
    recurringService.autoGenerateTransactions({
      lookAheadDays:60,maxPerRule:6,generateDailyTransactions,onProgress
    })
  );

  const handleSelectedGenerate = async (rule, occurrenceIds) => {
    const success = await runGeneration(onProgress =>
      recurringService.generateTransactionsWithOptions([rule.id], occurrenceIds, {
        generateFuture:true,generatePast:false,maxOccurrences:6,
        generateDailyTransactions,onProgress
      })
    );
    if(success)setSelectedRule(null);
    return success;
  };

  const animationProps = settings.reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.3 },
      };

  return (
    <motion.div className="space-y-5 pb-6 md:space-y-6" {...animationProps}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-950">Recurring Transactions</h1>
          <p className="mt-1 text-sm text-slate-600">Manage your recurring income and expenses with daily breakdown</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={handleAutoGenerate} disabled={isGenerating||recurringRules.length===0}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-emerald-400">
            {isGenerating?<FiIcons.FiLoader className="animate-spin"/>:<FiPlay/>} {isGenerating?'Generating...':'Auto Generate'}
          </button>
          <button type="button" onClick={()=>{setEditingRule(null);setShowAddForm(true)}} disabled={isGenerating}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#079de2] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#0176D3] disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-blue-400">
            <FiPlus/> Add Recurring Rule
          </button>
        </div>
      </div>

      {generationResult&&<div role="status" className={'rounded-xl border p-3 text-sm font-semibold '+
        (generationResult.success?'border-emerald-200 bg-emerald-50 text-emerald-900':'border-rose-200 bg-rose-50 text-rose-900')}>
        {generationResult.message}
      </div>}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="flex min-w-0 items-center gap-4 rounded-2xl border border-[#dce5f0] bg-white p-4 shadow-sm">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-sky-100 text-sky-600"><FiRepeat size={23}/></span>
          <div><p className="text-xs font-semibold text-slate-600">Active Rules</p><p className="mt-1 text-2xl font-extrabold text-slate-950">{activeRules.length}</p></div>
        </div>
        <div className="flex min-w-0 items-center gap-4 rounded-2xl border border-[#dce5f0] bg-white p-4 shadow-sm">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-600"><FiIcons.FiTrendingUp size={23}/></span>
          <div className="min-w-0"><p className="text-xs font-semibold text-slate-600">Monthly Income</p>
            <p className="mt-1 truncate text-xl font-extrabold tabular-nums text-emerald-700" title={formatCurrency(monthlyTotals.income/100)}>{formatCurrency(monthlyTotals.income/100)}</p>
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-4 rounded-2xl border border-[#dce5f0] bg-white p-4 shadow-sm">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-rose-50 text-rose-600"><FiIcons.FiTrendingDown size={23}/></span>
          <div className="min-w-0"><p className="text-xs font-semibold text-slate-600">Monthly Expenses</p>
            <p className="mt-1 truncate text-xl font-extrabold tabular-nums text-rose-600" title={formatCurrency(monthlyTotals.expenses/100)}>{formatCurrency(monthlyTotals.expenses/100)}</p>
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-3 rounded-2xl border border-[#dce5f0] bg-white p-4 shadow-sm">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-sky-100 text-sky-600"><FiCalendar size={23}/></span>
          <div className="min-w-0 flex-1"><p className="text-xs font-semibold text-slate-600">Next Scheduled</p>
            <p className="mt-1 truncate text-lg font-extrabold text-slate-950">{nextScheduled?format(nextScheduled,'MMM d, yyyy'):'No upcoming rule'}</p>
          </div>
          <span className="hidden rounded-full bg-sky-50 px-2 py-1 text-[10px] font-bold text-sky-700 2xl:inline">{generateDailyTransactions?'Daily Breakdown':'Period Total'}</span>
        </div>
      </div>

      <section className="rounded-2xl border border-[#dce5f0] bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <FiSettings size={22} className="shrink-0 text-[#385575]"/>
            <div><h2 className="text-sm font-bold text-slate-950">Transaction Generation Mode</h2>
              <p className="mt-0.5 text-xs text-slate-600">Choose how recurring transactions are generated</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex min-h-10 cursor-pointer items-center gap-2 text-sm text-slate-700">
              <input type="radio" name="generationMode" checked={!generateDailyTransactions}
                disabled={isGenerating} onChange={()=>setGenerateDailyTransactions(false)}
                className="h-4 w-4 accent-blue-600"/> Period Total
            </label>
            <label className="inline-flex min-h-10 cursor-pointer items-center gap-2 text-sm text-slate-700">
              <input type="radio" name="generationMode" checked={generateDailyTransactions}
                disabled={isGenerating} onChange={()=>setGenerateDailyTransactions(true)}
                className="h-4 w-4 accent-blue-600"/> Daily Breakdown
            </label>
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2.5 rounded-xl bg-[#edf7fd] px-3 py-3 text-xs leading-relaxed text-[#204a72]">
          <FiInfo size={17} className="mt-0.5 shrink-0 text-sky-600"/>
          <p>{generateDailyTransactions
            ? <><strong>Daily Breakdown Mode:</strong> Each recurring period is split into individual daily transactions. For example, a $300 monthly rent can create 30 daily transactions of $10 each.</>
            : <><strong>Period Total Mode:</strong> Each scheduled period creates one transaction for the full recurring amount.</>}
          </p>
        </div>
      </section>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <label className="relative block w-full lg:max-w-[420px]">
          <span className="sr-only">Search recurring rules</span>
          <FiIcons.FiSearch aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"/>
          <input type="search" value={search} onChange={e=>updateSearch(e.target.value)}
            placeholder="Search recurring rules..." aria-label="Search recurring rules"
            className="min-h-11 w-full rounded-2xl border border-[#d9e4f1] bg-[#f7f9fc] py-3 pl-11 pr-4 text-sm text-slate-800 outline-none focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"/>
        </label>
        <div className="flex flex-wrap gap-2" aria-label="Filter recurring rules">
          {filters.map(([key,label])=><button type="button" key={key}
            onClick={()=>setActiveFilter(key)} aria-pressed={activeFilter===key}
            className={'min-h-10 rounded-full border px-3.5 py-2 text-xs sm:text-sm font-semibold transition focus-visible:ring-2 focus-visible:ring-blue-400 '+(
              activeFilter===key?'border-[#079de2] bg-[#079de2] text-white shadow-sm':
              key==='income'?'border-emerald-100 bg-emerald-50 text-emerald-700 hover:bg-emerald-100':
              key==='expense'?'border-rose-100 bg-rose-50 text-rose-700 hover:bg-rose-100':
              'border-[#dce5f0] bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50'
            )}>{label}</button>)}
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
        {visibleRules.map(rule=>{
          const nextOccurrence=calculateNextOccurrence(rule);
          const coverageDays=getCoverageDays(rule.frequency);
          const dailyAmount=calculateDailyAmount(rule);
          const expired=Boolean(rule.endDate&&new Date(String(rule.endDate).slice(0,10)+'T23:59:59')<new Date());
          const income=rule.type==='income';
          return <motion.article key={rule.id} {...animationProps}
            className={'min-w-0 rounded-2xl border bg-white p-4 sm:p-5 shadow-[0_3px_10px_rgba(15,43,82,0.045)] transition-shadow hover:shadow-md '+
              (income?'border-emerald-200':'border-[#dce5f0]')}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <span className={'grid h-10 w-10 shrink-0 place-items-center rounded-xl '+(income?'bg-emerald-100 text-emerald-600':'bg-rose-100 text-rose-600')}>
                  <FiRepeat size={20}/>
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-extrabold text-slate-950">{rule.name}</h3>
                    <span className={'rounded-full px-2.5 py-1 text-[10px] font-bold '+(income?'bg-emerald-100 text-emerald-800':'bg-rose-100 text-rose-700')}>{rule.type}</span>
                    {expired&&<span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600">Expired</span>}
                  </div>
                  <p className="mt-1 truncate text-xs text-slate-600">{rule.category}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button type="button" aria-label={'Generate '+rule.name} onClick={()=>setSelectedRule(rule)}
                  disabled={isGenerating||expired}
                  className="inline-flex min-h-10 items-center gap-1.5 rounded-lg bg-sky-100 px-3 py-2 text-xs font-bold text-sky-700 transition hover:bg-sky-200 disabled:cursor-not-allowed disabled:opacity-40">
                  <FiPlay size={14}/> Generate
                </button>
                <button type="button" aria-label={'Edit '+rule.name} title={'Edit '+rule.name}
                  onClick={()=>setEditingRule(rule)} disabled={isGenerating}
                  className="grid h-10 w-10 place-items-center rounded-lg text-[#385575] transition hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40">
                  <FiEdit2 size={17}/>
                </button>
                <button type="button" aria-label={'Delete '+rule.name} title={'Delete '+rule.name}
                  onClick={()=>setShowDeleteConfirm(rule)} disabled={isGenerating}
                  className="grid h-10 w-10 place-items-center rounded-lg text-rose-600 transition hover:bg-rose-50 disabled:opacity-40">
                  <FiTrash2 size={17}/>
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-5">
              {[
                ['Total Amount',formatCurrency(rule.amount)],
                ['Frequency',rule.frequency],
                ['Daily Rate',formatCurrency(dailyAmount)],
                ['Coverage Days',coverageDays+' days'],
                ['Next Occurrence',format(nextOccurrence,'MMM d, yyyy')]
              ].map(([label,value],index)=><div key={label} className={'min-w-0 '+(index?'sm:border-l sm:border-slate-100 sm:pl-2':'')}>
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
                <p className={'mt-1 break-words text-xs sm:text-sm font-bold tabular-nums capitalize '+(
                  index===0?(income?'text-emerald-700':'text-rose-600'):'text-slate-900'
                )}>{value}</p>
              </div>)}
            </div>

            {generateDailyTransactions && rule.frequency!=='daily'&&
              <p className="mt-4 rounded-lg bg-[#f4f7fb] px-3 py-2.5 text-xs leading-relaxed text-slate-600">
                <strong className="text-slate-800">Daily Breakdown Preview: </strong>
                Next period will generate <strong>{coverageDays} daily transactions</strong> of <strong>{formatCurrency(dailyAmount)}</strong> each, covering {format(nextOccurrence,'MMM d')} to {format(addDays(nextOccurrence,coverageDays-1),'MMM d, yyyy')}.
              </p>}
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#385575]">
              <span className="inline-flex items-center gap-1.5"><FiCalendar className="shrink-0"/> Start: {format(new Date(rule.startDate),'MMM d, yyyy')}</span>
              <span className="inline-flex items-center gap-1.5"><FiRepeat className="shrink-0"/> Recurring {rule.name}</span>
              {rule.endDate&&<span>End: {format(new Date(rule.endDate),'MMM d, yyyy')}</span>}
              {rule.person&&<span>Person: {rule.person}</span>}
            </div>
            {rule.notes&&<p className="mt-3 break-words text-xs text-slate-500">{rule.notes}</p>}
            {rule.lastGeneratedAt&&<p className="mt-2 text-xs text-slate-500">
              Last generated: {format(new Date(rule.lastGeneratedAt),'MMM d, yyyy HH:mm')}
            </p>}
          </motion.article>;
        })}
        {!visibleRules.length&&<div className="col-span-full rounded-2xl border border-[#dce5f0] bg-white p-10 text-center">
          <FiRepeat size={40} className="mx-auto mb-3 text-slate-400"/>
          <h3 className="text-lg font-bold text-slate-900">{recurringRules.length?'No rules match your filters':'No recurring rules yet'}</h3>
          <p className="mt-2 text-sm text-slate-500">{recurringRules.length?'Try another search or category.':'Set up recurring transactions to automate income and expense tracking.'}</p>
          {!recurringRules.length&&<button type="button" onClick={()=>setShowAddForm(true)}
            className="mt-5 rounded-xl bg-[#0176D3] px-4 py-3 text-sm font-bold text-white">Add Your First Recurring Rule</button>}
        </div>}
      </div>

      {/* Loading Overlay */}
      {generationProgress && <RecurringGenerationProgress progress={generationProgress} />}

      {/* Modals */}
      <AnimatePresence>
        {/* Schedule Preview Modal */}
        {selectedRule && (
          <SchedulePreview
            rule={selectedRule}
            onClose={() => setSelectedRule(null)}
            formatCurrency={formatCurrency}
            generateDailyTransactions={generateDailyTransactions}
            onGenerate={(ids) => handleSelectedGenerate(selectedRule, ids)}
            parentGenerating={isGenerating}
          />
        )}

        {/* Create and edit use the existing IndexedDB recurringRules collection. */}
        {(showAddForm||editingRule)&&(
          <AddRecurringForm
            key={editingRule?'edit-'+editingRule.id:'new'}
            initialRule={editingRule}
            onClose={()=>{setShowAddForm(false);setEditingRule(null)}}
            onSave={async fields=>{
              if(editingRule){
                const saved=await dbService.updateRecurringRule(editingRule.id,fields);
                if(!saved)throw new Error('Recurring rule was not found. Nothing was changed.');
                actions.updateRecurringRule(saved);
                setEditingRule(null);
              }else{
                const savedId=await dbService.addRecurringRule(fields);
                actions.addRecurringRule({...fields,id:savedId});
                setShowAddForm(false);
              }
            }}
          />
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <ConfirmDialog
            title="Delete Recurring Rule"
            message={`Are you sure you want to delete "${showDeleteConfirm.name}"? This will also delete all transactions generated from this rule.`}
            confirmText="Delete Rule"
            confirmStyle="danger"
            onConfirm={() => handleDeleteRule(showDeleteConfirm.id)}
            onCancel={() => setShowDeleteConfirm(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Schedule Preview Component
const SchedulePreview = ({ rule, onClose, formatCurrency, generateDailyTransactions, onGenerate, parentGenerating }) => {
  const { state } = useApp();
  const { settings } = state;
  const [selectedOccurrences, setSelectedOccurrences] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);

  // Generate next 6 occurrences
  const occurrences = recurringService.generateOccurrences(rule, {
    generateFuture: true,
    generatePast: false,
    maxOccurrences: 6,
  });

  const handleSelectOccurrence = (id) => {
    setSelectedOccurrences(prev => {
      if (prev.includes(id)) {
        return prev.filter(occId => occId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedOccurrences.length === occurrences.length) {
      setSelectedOccurrences([]);
    } else {
      setSelectedOccurrences(occurrences.map(occ => occ.id));
    }
  };

  const handleGenerate = async () => {
    if (selectedOccurrences.length === 0 || isGenerating || parentGenerating) return;
    setIsGenerating(true);
    try { await onGenerate(selectedOccurrences); }
    finally { setIsGenerating(false); }
  };

  const getTotalTransactionsForOccurrences = () => {
    if (!generateDailyTransactions || rule.frequency === 'daily') {
      return selectedOccurrences.length;
    }
    
    return selectedOccurrences.reduce((total, occId) => {
      const occurrence = occurrences.find(occ => occ.id === occId);
      return total + (occurrence ? occurrence.period.days : 0);
    }, 0);
  };

  const animationProps = settings.reducedMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        transition: { duration: 0.3 },
      };

  return (
    <motion.div
      className="mobile-full-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={parentGenerating ? undefined : onClose}
      {...animationProps}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Schedule Preview: {rule.name}
          </h2>
          <p className="text-gray-600 mt-1">
            Select occurrences to generate transactions
            {generateDailyTransactions && rule.frequency !== 'daily' && (
              <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                Daily Breakdown Mode
              </span>
            )}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          <div className="mb-4 flex items-center justify-between">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedOccurrences.length === occurrences.length}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-600">
                Select all ({occurrences.length} occurrences)
              </span>
            </label>
            
            {generateDailyTransactions && rule.frequency !== 'daily' && selectedOccurrences.length > 0 && (
              <div className="text-sm text-gray-600">
                Will generate <strong>{getTotalTransactionsForOccurrences()}</strong> daily transactions
              </div>
            )}
          </div>

          <div className="space-y-3">
            {occurrences.map((occurrence) => (
              <div
                key={occurrence.id}
                className={`border rounded-lg p-4 ${
                  selectedOccurrences.includes(occurrence.id)
                    ? 'border-primary-300 bg-primary-50'
                    : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedOccurrences.includes(occurrence.id)}
                      onChange={() => handleSelectOccurrence(occurrence.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
                    />
                    <div>
                      <p className="font-medium text-gray-900">
                        {format(occurrence.date, 'MMM d, yyyy')}
                      </p>
                      <p className="text-sm text-gray-600">
                        Coverage: {format(occurrence.date, 'MMM d')} - {format(occurrence.endDate, 'MMM d')}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                        <span>{occurrence.period.days} days</span>
                        <span>{formatCurrency(occurrence.dailyRate)}/day</span>
                        {generateDailyTransactions && rule.frequency !== 'daily' && (
                          <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full">
                            {occurrence.period.days} daily transactions
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${
                      rule.type === 'income' ? 'text-success-600' : 'text-danger-600'
                    }`}>
                      {formatCurrency(occurrence.amount)}
                    </p>
                    <div className="flex items-center text-xs text-gray-500 mt-1">
                      <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                      {occurrence.status}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            disabled={isGenerating || parentGenerating}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 rounded-lg font-medium transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={selectedOccurrences.length === 0 || isGenerating}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors duration-200"
          >
            {isGenerating ? (
              <div className="flex items-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Generating...
              </div>
            ) : (
              `Generate ${getTotalTransactionsForOccurrences()} Transaction${getTotalTransactionsForOccurrences() !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const RecurringFormSection=({icon:Icon,title,subtitle,children})=><section className="rounded-2xl border border-slate-200 p-4 md:p-5 bg-white"><div className="flex gap-3 mb-5 items-center"><span className="p-2.5 bg-blue-50 text-blue-600 rounded-xl"><Icon size={19}/></span><div><h3 className="font-bold text-sm text-slate-900">{title}</h3><p className="text-xs text-slate-500">{subtitle}</p></div></div>{children}</section>;
// The form remains backed by the existing recurring rule service and collection.
const AddRecurringForm = ({onClose,onSave,initialRule=null}) => {
 const {state}=useApp();const {categories=[],people=[],settings}=state;
 const editing=Boolean(initialRule);
 const dateField=value=>!value?'':(typeof value==='string'?value.slice(0,10):format(new Date(value),'yyyy-MM-dd'));
 const [form,setForm]=useState(()=>initialRule?{
  name:initialRule.name||'',type:initialRule.type||'expense',
  amount:String(initialRule.amount??''),category:initialRule.category||'',
  frequency:initialRule.frequency||'monthly',startDate:dateField(initialRule.startDate),
  endDate:dateField(initialRule.endDate),notes:initialRule.notes||'',
  person:initialRule.person||'',payee:initialRule.payee||'',
  tags:Array.isArray(initialRule.tags)?initialRule.tags.join(', '):String(initialRule.tags||'')
 }:{name:'',type:'expense',amount:'',category:'',frequency:'monthly',
    startDate:format(new Date(),'yyyy-MM-dd'),endDate:'',notes:'',person:'',payee:'',tags:''});
 const [daily,setDaily]=useState(initialRule?initialRule.generateDailyTransactions!==false:true),
   [saving,setSaving]=useState(false),[error,setError]=useState('');
 const set=(field,value)=>setForm(p=>({...p,[field]:value,...(field==='type'?{category:''}:{})}));
 const change=e=>set(e.target.name,e.target.value);
 const money=n=>new Intl.NumberFormat('en-AU',{style:'currency',currency:settings.currency||'AUD'}).format(Number(n)||0);
 const frequencies=['daily','weekly','fortnightly','monthly','quarterly','yearly'];
 const monthly={daily:365.2425/12,weekly:52.1775/12,fortnightly:26.08875/12,monthly:1,quarterly:1/3,yearly:1/12};
 const estimate=Number(form.amount||0)*(monthly[form.frequency]||1);
 const labelClass='block text-xs font-bold text-slate-700 mb-2';
 const inputClass='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500';
 const categoriesForType=categories.filter(c=>c.type===form.type);
 if(form.category&&!categoriesForType.some(c=>c.name===form.category))categoriesForType.push({id:'existing-category',name:form.category});
 const submit=async e=>{e.preventDefault();if(saving||!form.name.trim()||!(Number(form.amount)>0)||!form.category||!form.startDate)return;if(form.endDate&&form.endDate<form.startDate){setError('End date must be on or after the start date.');return;}setSaving(true);setError('');try{await onSave({...form,name:form.name.trim(),amount:Number(form.amount),tags:form.tags.split(',').map(s=>s.trim()).filter(Boolean),generateDailyTransactions:daily});}catch(err){setError(err.message||'Unable to save recurring rule');setSaving(false)}};
 return <motion.div initial={{opacity:0}} animate={{opacity:1}} className="mobile-full-modal fixed inset-0 bg-slate-900/55 backdrop-blur-[3px] z-50 flex items-center justify-center p-2 sm:p-5" onMouseDown={e=>{if(e.target===e.currentTarget)onClose()}}>
 <div role="dialog" aria-modal="true" aria-labelledby="recurring-modal-title" className="w-full max-w-[975px] max-h-[95vh] overflow-hidden flex flex-col bg-[#fbfcfe] rounded-[20px] shadow-2xl">
 <header className="bg-white px-5 md:px-7 py-5 border-b border-slate-200 flex justify-between items-center gap-4 shrink-0"><div className="flex gap-4 items-center"><span className="w-14 h-14 rounded-2xl bg-blue-50 grid place-items-center text-blue-600"><FiRepeat size={27}/></span><div><h2 id="recurring-modal-title" className="text-xl font-extrabold text-slate-900">{editing?'Edit Recurring Rule':'Add Recurring Rule'}</h2><p className="text-sm text-slate-500">{editing?'Changes apply to future rule generation; existing transactions remain unchanged.':'Set up recurring transactions to automate your finances.'}</p></div></div><button type="button" aria-label="Close form" onClick={onClose} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><FiIcons.FiX size={21}/></button></header>
 <form id="recurring-create" onSubmit={submit} className="flex-1 overflow-y-auto p-4 md:p-5">
 <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4"><div className="space-y-3">
 <RecurringFormSection icon={FiIcons.FiFileText} title="Basic Information" subtitle="Tell us about this recurring transaction.">
 <label className={labelClass} htmlFor="rule-name">Name *</label><input id="rule-name" name="name" required value={form.name} onChange={change} placeholder="e.g., Monthly Rent" className={inputClass}/>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4"><div><label className={labelClass}>Type *</label><div className="flex gap-2">{['expense','income'].map(t=><button key={t} type="button" onClick={()=>set('type',t)} aria-pressed={form.type===t} className={'flex-1 rounded-xl border py-2.5 text-xs font-semibold flex items-center justify-center gap-2 '+(form.type===t?(t==='expense'?'border-rose-300 bg-rose-50 text-rose-600':'border-emerald-300 bg-emerald-50 text-emerald-600'):'border-slate-200 text-slate-600')}>{t==='expense'?<FiIcons.FiTrendingDown/>:<FiIcons.FiTrendingUp/>}{t==='expense'?'Expense':'Income'}</button>)}</div></div><div><label htmlFor="rule-amount" className={labelClass}>Amount *</label><div className="relative"><span className="absolute left-3 top-2.5 text-slate-400">$</span><input id="rule-amount" type="number" min=".01" step=".01" name="amount" value={form.amount} onChange={change} placeholder="0.00" required className={inputClass+' pl-8'}/></div></div></div>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4"><div><label className={labelClass} htmlFor="rule-category">Category *</label><select id="rule-category" name="category" value={form.category} required onChange={change} className={inputClass}><option value="">Select category</option>{categoriesForType.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}</select></div><div><label className={labelClass} htmlFor="rule-payee">Payee</label><input id="rule-payee" name="payee" value={form.payee} onChange={change} placeholder="e.g., Real Estate Agent" className={inputClass}/></div></div>
 </RecurringFormSection>
 <RecurringFormSection icon={FiCalendar} title="Schedule" subtitle="Choose how often this transaction occurs."><div className="grid grid-cols-1 sm:grid-cols-3 gap-3"><div><label className={labelClass} htmlFor="rule-frequency">Frequency *</label><select id="rule-frequency" name="frequency" value={form.frequency} onChange={change} className={inputClass}>{frequencies.map(f=><option key={f} value={f}>{f[0].toUpperCase()+f.slice(1)}</option>)}</select></div><div><label className={labelClass} htmlFor="rule-start">Start Date *</label><input id="rule-start" type="date" name="startDate" value={form.startDate} required onChange={change} className={inputClass}/></div><div><label className={labelClass} htmlFor="rule-end">End Date</label><input id="rule-end" type="date" min={form.startDate} name="endDate" value={form.endDate} onChange={change} className={inputClass}/></div></div></RecurringFormSection>
 <RecurringFormSection icon={FiIcons.FiList} title="Additional Details" subtitle="Add more context and options."><div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><div><label className={labelClass} htmlFor="rule-person">Person</label><select id="rule-person" name="person" value={form.person} onChange={change} className={inputClass}><option value="">None</option>{people.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}</select></div><div><label className={labelClass} htmlFor="rule-tags">Tags</label><input id="rule-tags" name="tags" value={form.tags} onChange={change} placeholder="rent, housing, essential" className={inputClass}/></div></div><div className="mt-4"><label className={labelClass} htmlFor="rule-notes">Notes</label><textarea id="rule-notes" rows={2} name="notes" value={form.notes} onChange={change} placeholder="Optional notes..." className={inputClass}/></div></RecurringFormSection>
 <label className="rounded-2xl bg-blue-50 border border-blue-100 flex items-center gap-4 p-4 cursor-pointer"><input type="checkbox" checked={daily} onChange={e=>setDaily(e.target.checked)} className="h-5 w-5 accent-blue-600"/><span><strong className="block text-sm text-slate-900">Generate daily breakdown transactions</strong><span className="block text-xs text-slate-500 mt-1">Create daily accrual allocations when generating transactions from this rule.</span></span></label>
 </div><aside className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 self-start lg:sticky lg:top-0"><div className="flex items-center gap-3 mb-5"><span className="bg-blue-50 p-2.5 rounded-xl text-blue-600"><FiIcons.FiEye/></span><div><h3 className="font-bold text-sm">Preview & Summary</h3><p className="text-xs text-slate-500">How your recurring rule will appear.</p></div></div><div className="border border-slate-200 rounded-2xl p-4"><div className="flex gap-3 items-center"><span className={'rounded-xl p-3 '+(form.type==='expense'?'bg-rose-50 text-rose-500':'bg-emerald-50 text-emerald-600')}><FiRepeat size={24}/></span><div><strong className="block text-sm text-slate-900">{form.name||'New recurring rule'}</strong><span className={'text-xs rounded-full px-2 py-1 '+(form.type==='expense'?'bg-rose-50 text-rose-600':'bg-emerald-50 text-emerald-600')}>{form.type==='expense'?'Expense':'Income'}</span></div></div><h3 className="text-3xl font-extrabold text-slate-950 mt-5">{money(form.amount)}</h3><p className="text-sm text-slate-500 capitalize">per {form.frequency==='fortnightly'?'fortnight':form.frequency.replace('ly','')}</p><div className="border-t border-slate-100 mt-5 pt-4 space-y-4">{[[FiCalendar,'Start date',form.startDate||'Not selected'],[FiRepeat,'Frequency',form.frequency],[FiIcons.FiFolder,'Category',form.category||'Not selected'],[FiIcons.FiUser,'Payee',form.payee||'Not set'],[FiIcons.FiUsers,'Person',form.person||'None'],[FiIcons.FiTag,'Tags',form.tags||'None']].map(([Icon,key,val])=><div key={key} className="flex gap-3 items-center text-sm"><Icon className="text-slate-400 shrink-0"/><div><span className="block text-xs text-slate-400">{key}</span><strong className="text-slate-800 font-medium capitalize break-all">{val}</strong></div></div>)}</div><div className="bg-blue-50 rounded-xl p-4 mt-5"><p className="text-xs text-slate-500">Estimated monthly impact</p><strong className="text-xl font-extrabold text-blue-600">{money(estimate)}</strong><p className="text-xs text-slate-500 mt-2">Approximation for planning only. Actual month allocation depends on scheduled coverage dates.</p></div></div></aside></div>
 {error&&<p className="mt-3 text-sm text-rose-600" role="alert">{error}</p>}
 </form>
 <footer className="mobile-modal-actions flex justify-end gap-3 border-t border-slate-200 bg-white p-4 md:px-6 shrink-0"><button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button><button type="submit" form="recurring-create" disabled={saving||!form.name.trim()||!(Number(form.amount)>0)||!form.category} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2"><FiIcons.FiSave/>{saving?'Saving...':editing?'Save Changes':'Save Recurring Rule'}</button></footer></div></motion.div>;
};
export default Recurring;